import type React from 'react';
import type {GroupCompanies} from '../../../../core/entities/companyDetail/schema';
import type {GetCompanyInfoResponse} from '../../../../core/repositories/companiesRepository';
import type {
  GetCompanyDetailResponse,
  GetCompanyPageColumnPostsResponse,
  GetNativeAdPostsResponse,
  GetRecruitmentsResponse,
} from '../../../../core/repositories/companyDetailRepository';
import type {Company} from '../../../../core/repositories/companyRepository';
import type {Recruitment} from '../../../../core/repositories/recruitingInfoRepository';
import {useWindowSize} from '../../../../shared/services/useWindowSize';
import type {Heading, IsContents} from '../../usecase/useCompanyDetailContents';
import {EntryButtonPC} from '../EntryButton/pc';
import {CompanyInfoTable} from './Presenter/CompanyInfoTable';
import {CompanyInterviews} from './Presenter/CompanyInterviews';
import {CompanyIntroduction} from './Presenter/CompanyIntroduction';
import {CompanyOfficeGallery} from './Presenter/CompanyOfficeGallery';
import {CompanyPageRecruitmentList} from './Presenter/CompanyPageRecruitmentList';
import {CompanyRecruitmentGuideTable} from './Presenter/CompanyRecruitmentGuideTable';
import {CompanySpecialPosts} from './Presenter/CompanySpecialPosts';
import {CompanyWebSeminar} from './Presenter/CompanyWebSeminar';
import {FollowedAttribute} from './Presenter/FollowedAttribute';
import {NativeAds} from './Presenter/NativeAds';
import {OfferedAttribute} from './Presenter/OfferedAttribute';
import {RelatedCommunities} from './Presenter/RelatedCommunities';
import {RelatedGroupCompanies} from './Presenter/RelatedGroupCompanies';
import {SameIndustryCompanies} from './Presenter/SameIndustryCompanies';
import {SectionTitle} from './Presenter/SectionTitle';

type Props = {
  companyDetail: GetCompanyDetailResponse;
  companyInfo: GetCompanyInfoResponse;
  sections: Heading[];
  nativeAds?: GetNativeAdPostsResponse;
  recruitments?: GetRecruitmentsResponse;
  filteredRecruitments?: Recruitment[];
  sameIndustryCompanies?: Company[];
  groupCompanies?: GroupCompanies;
  companyPageColumnPosts?: GetCompanyPageColumnPostsResponse;
  isPreview?: boolean;
  isPC: boolean;
  isLogin: boolean;
  entryURLForTracking: string;
  recruitingFairRegistrationURLForTracking: string;
  isContents: IsContents;
};

export const CompanyDetailBody: React.FC<Props> = ({
  companyDetail,
  companyInfo,
  sections,
  filteredRecruitments,
  nativeAds,
  companyPageColumnPosts,
  sameIndustryCompanies,
  groupCompanies,
  isPC,
  isLogin,
  entryURLForTracking,
  recruitingFairRegistrationURLForTracking,
  isContents,
}) => {
  const {width} = useWindowSize();

  const getSectionName = (id: string) =>
    sections.find((s) => s.id === id)?.tagname ?? '';

  return (
    <div className="p-16 pc:p-0" style={{width: isPC ? 1200 : width}}>
      {companyDetail.introduction !== undefined && (
        <CompanyIntroduction
          title={getSectionName('introduction')}
          companyPageSharedParagraph={companyDetail.introduction.paragraphs}
          description={companyDetail.introduction.description}
          isPC={isPC}
        />
      )}
      <NativeAdsSection
        companyDetail={companyDetail}
        nativeAds={nativeAds}
        entryURLForTracking={entryURLForTracking}
      />
      {isLogin &&
      companyDetail?.webSeminar &&
      companyDetail.webSeminar.webSeminars.length > 0 ? (
        <div className="mt-8 pc:mt-16">
          <SectionTitle
            id="web-seminar"
            sectionTitle={getSectionName('web-seminar')}
          />
          <div className="flex flex-col pc:flex-row pc:justify-between mt-8 gap-8">
            {companyDetail.webSeminar.webSeminars.map((webSeminnar) => (
              <CompanyWebSeminar
                key={webSeminnar.id}
                webSeminarID={webSeminnar.id}
                webSeminarThumbnailURL={webSeminnar.thumbnailURL}
                webSeminarTitle={webSeminnar.title}
                companyName={companyDetail.metaData?.companyName}
                description={webSeminnar.description}
                isOriginal={webSeminnar.isOriginal}
                isFreeListing={webSeminnar.isFreeListing}
                isPrMovie={webSeminnar.isPrMovie}
              />
            ))}
          </div>
        </div>
      ) : null}
      <CompanyInterviewsSection
        title={getSectionName('interview')}
        companyDetail={companyDetail}
        companyInfo={companyInfo}
      />
      <CompanyOfficeGallerySection
        title={getSectionName('office-gallery')}
        companyDetail={companyDetail}
        companyInfo={companyInfo}
        isContents={isContents}
      />
      <RecruitmentGuidelineSection
        title={getSectionName('recruitment-guide')}
        companyDetail={companyDetail}
        isContents={isContents}
      />
      <CompanyInfoSection
        title={getSectionName('company-data')}
        companyDetail={companyDetail}
        entryURLForTracking={entryURLForTracking}
        recruitingFairRegistrationURLForTracking={
          recruitingFairRegistrationURLForTracking
        }
      />
      <RecruitmentsSection
        title={getSectionName('recruitment')}
        filteredRecruitments={filteredRecruitments}
      />
      <CompanySpecialPostsSection
        companyDetail={companyDetail}
        companyPageColumnPosts={companyPageColumnPosts}
      />
      <RelatedCommunitiesSection
        companyDetail={companyDetail}
        isContents={isContents}
      />
      <OfferedAttributeSection
        companyDetail={companyDetail}
        isContents={isContents}
      />
      <FollowedAttributeSection
        companyDetail={companyDetail}
        isContents={isContents}
      />
      <RelatedGroupCompaniesSection
        companyDetail={companyDetail}
        groupCompanies={groupCompanies}
        isContents={isContents}
      />
      <SameIndustryCompaniesSection
        sameIndustryCompanies={sameIndustryCompanies}
        isContents={isContents}
      />
    </div>
  );
};

const CompanyInterviewsSection: React.FC<{
  companyDetail: GetCompanyDetailResponse;
  title: string;
  companyInfo: GetCompanyInfoResponse;
}> = ({companyDetail, title, companyInfo}) => (
  <>
    {companyDetail.companyInterview && (
      <div className="mt-16">
        <SectionTitle id="interview" sectionTitle={title} />
        <CompanyInterviews
          companyName={companyInfo.name}
          interviews={companyDetail.companyInterview.interviews}
        />
      </div>
    )}
  </>
);

const CompanyOfficeGallerySection: React.FC<{
  title: string;
  companyDetail: GetCompanyDetailResponse;
  companyInfo: GetCompanyInfoResponse;
  isContents: Props['isContents'];
}> = ({title, companyDetail, companyInfo, isContents}) => (
  <>
    {isContents.isOfficeGallery && (
      <div className="mt-16">
        <SectionTitle id="office-gallery" sectionTitle={title} />
        <CompanyOfficeGallery
          companyName={companyInfo.name}
          officeGallerys={companyDetail.officeGallery?.pictures}
        />
      </div>
    )}
  </>
);

const NativeAdsSection: React.FC<{
  companyDetail: GetCompanyDetailResponse;
  nativeAds?: GetNativeAdPostsResponse;
  entryURLForTracking: string;
}> = ({companyDetail, nativeAds, entryURLForTracking}) => (
  <>
    {nativeAds && (
      <>
        <NativeAds
          companyName={companyDetail.metaData?.companyName}
          nativeAds={nativeAds}
        />
        <EntryButtonPC
          entryURL={entryURLForTracking}
          registrationURL={
            companyDetail.metaData?.recruitingFairRegistrationURL
          }
        />
      </>
    )}
  </>
);

const RecruitmentGuidelineSection: React.FC<{
  title: string;
  companyDetail: GetCompanyDetailResponse;
  isContents: Props['isContents'];
}> = ({title, companyDetail, isContents}) => (
  <>
    {isContents.isRecruitmentGuideline &&
      companyDetail.recruitmentGuideline && (
        <div className="mt-8 pc:mt-16">
          <SectionTitle id="recruitment-guide" sectionTitle={title} />
          <CompanyRecruitmentGuideTable
            employmentType={companyDetail.recruitmentGuideline.employmentType}
            jobType={companyDetail.recruitmentGuideline.jobType}
            jobDescription={companyDetail.recruitmentGuideline.jobDescription}
            salary={companyDetail.recruitmentGuideline.salary}
            benefits={companyDetail.recruitmentGuideline.benefits}
            workLocation={companyDetail.recruitmentGuideline.workLocation}
            educationSystem={companyDetail.recruitmentGuideline.educationSystem}
            qualificationRequirements={
              companyDetail.recruitmentGuideline.qualificationRequirements
            }
            companyPageYearlyItem={
              companyDetail.recruitmentGuideline.extraItems
            }
            contactUrl={companyDetail.recruitmentGuideline.contactURL}
          />
        </div>
      )}
  </>
);

const CompanyInfoSection: React.FC<{
  title: string;
  companyDetail: GetCompanyDetailResponse;
  entryURLForTracking: string;
  recruitingFairRegistrationURLForTracking: string;
}> = ({
  title,
  companyDetail,
  entryURLForTracking,
  recruitingFairRegistrationURLForTracking,
}) => (
  <>
    {companyDetail.companyData && (
      <>
        <div className="mt-8 pc:mt-16">
          <SectionTitle id="company-data" sectionTitle={title} />
          <CompanyInfoTable
            establishmentDate={companyDetail.companyData.establishmentDate}
            businessDescription={companyDetail.companyData.businessDescription}
            websiteUrl={companyDetail.companyData.webSiteURL}
            address={companyDetail.companyData.address}
            capital={companyDetail.companyData.capital}
            representativePerson={
              companyDetail.companyData.representativePerson
            }
            employeesCount={companyDetail.companyData.employeesCount}
            extraItems={companyDetail.companyData.extraItems}
          />
        </div>
        <EntryButtonPC
          entryURL={entryURLForTracking}
          registrationURL={recruitingFairRegistrationURLForTracking}
        />
      </>
    )}
  </>
);

const RecruitmentsSection: React.FC<{
  title: string;
  filteredRecruitments?: Recruitment[];
}> = ({title, filteredRecruitments}) => {
  return (
    <>
      {filteredRecruitments && filteredRecruitments.length > 0 ? (
        <div className="mt-16">
          <SectionTitle id="recruitment" sectionTitle={title} />
          <CompanyPageRecruitmentList recruitmentList={filteredRecruitments} />
        </div>
      ) : null}
    </>
  );
};

const CompanySpecialPostsSection: React.FC<{
  companyDetail: GetCompanyDetailResponse;
  companyPageColumnPosts?: GetCompanyPageColumnPostsResponse;
}> = ({companyDetail, companyPageColumnPosts}) => (
  <>
    {companyPageColumnPosts?.posts &&
      companyPageColumnPosts.posts.length > 0 && (
        <CompanySpecialPosts
          companyName={companyDetail.metaData?.companyName}
          specialPosts={companyPageColumnPosts}
        />
      )}
  </>
);

const RelatedCommunitiesSection: React.FC<{
  companyDetail: GetCompanyDetailResponse;
  isContents: Props['isContents'];
}> = ({companyDetail, isContents}) => (
  <>
    {companyDetail.metaData &&
      companyDetail.relatedCommunity?.relatedCommunities &&
      isContents.isRelatedCommunities && (
        <RelatedCommunities
          companyID={companyDetail.metaData?.companyID}
          companyName={companyDetail.metaData?.companyName}
          relatedCommunities={companyDetail.relatedCommunity.relatedCommunities}
        />
      )}
  </>
);

const OfferedAttributeSection: React.FC<{
  companyDetail: GetCompanyDetailResponse;
  isContents: Props['isContents'];
}> = ({companyDetail, isContents}) => (
  <>
    {companyDetail.offeredUsersAttribute &&
      isContents.isOfferedUsersAttribute &&
      companyDetail.offeredUsersAttribute.usersCount > 4 && (
        <OfferedAttribute
          sex={companyDetail.offeredUsersAttribute.sex}
          universities={companyDetail.offeredUsersAttribute.university}
          toeic={companyDetail.offeredUsersAttribute.toeic}
          internCompanies={companyDetail.offeredUsersAttribute.internCompany}
          companyID={companyDetail.metaData?.companyID}
        />
      )}
  </>
);

const FollowedAttributeSection: React.FC<{
  companyDetail: GetCompanyDetailResponse;
  isContents: Props['isContents'];
}> = ({companyDetail, isContents}) => (
  <>
    {companyDetail.followUsersAttribute &&
      isContents.isFollowUsersAttribute && (
        <FollowedAttribute
          title={'フォロワーの属性'}
          universities={companyDetail.followUsersAttribute.university}
          sex={companyDetail.followUsersAttribute.sex}
          bunri={companyDetail.followUsersAttribute.bunri}
          graduate={companyDetail.followUsersAttribute.univType}
          companyID={companyDetail.metaData?.companyID}
        />
      )}
  </>
);

const RelatedGroupCompaniesSection: React.FC<{
  companyDetail: GetCompanyDetailResponse;
  groupCompanies?: GroupCompanies;
  isContents: Props['isContents'];
}> = ({companyDetail, groupCompanies, isContents}) => (
  <div>
    {isContents.isGroupCompanies && groupCompanies && (
      <RelatedGroupCompanies
        companyName={companyDetail.metaData?.companyName}
        groupCompanies={groupCompanies}
      />
    )}
  </div>
);

const SameIndustryCompaniesSection: React.FC<{
  sameIndustryCompanies?: Company[];
  isContents: Props['isContents'];
}> = ({sameIndustryCompanies, isContents}) => (
  <div>
    {isContents.isSameIndustryCompanies && sameIndustryCompanies && (
      <SameIndustryCompanies sameIndustryCompanies={sameIndustryCompanies} />
    )}
  </div>
);
